import { Marked } from 'marked';
import DOMPurify from 'dompurify';

// One shared parser instance so options here can't collide with other marked
// consumers (the TipTap markdown extension configures its own instance).
// `breaks: true` because element values are user notes, where a single
// newline is expected to render as a line break.
const parser = new Marked({ gfm: true, breaks: true });

// Tags a rich-text element can produce (toolbar + markdown equivalents).
// Sanitization is an allowlist: anything else — scripts, iframes, event
// handlers, style — is stripped even if hand-written in markdown mode.
const ALLOWED_TAGS = [
	'p', 'br', 'strong', 'b', 'em', 'i', 'del', 's', 'code', 'pre',
	'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
	'ul', 'ol', 'li', 'blockquote', 'a', 'hr',
];
const ALLOWED_ATTR = ['href', 'title', 'start'];

/**
 * Render a markdown string to sanitized HTML. Returns '' for empty input.
 * Safe to inject via dangerouslySetInnerHTML.
 */
export function renderMarkdown(markdown) {
	if (typeof markdown !== 'string' || markdown.trim() === '') return '';
	const html = parser.parse(markdown, { async: false });
	return DOMPurify.sanitize(html, {
		ALLOWED_TAGS,
		ALLOWED_ATTR,
		// Links open in a new tab; DOMPurify hook below adds rel/target.
	});
}

// Force safe link behavior on every render (DOMPurify strips these otherwise
// unlisted attributes AFTER hooks run, so allow them explicitly instead).
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
	if (node.tagName === 'A') {
		node.setAttribute('target', '_blank');
		node.setAttribute('rel', 'noopener noreferrer');
	}
});

/**
 * Reduce markdown to its readable text (for display templates and previews):
 * render, sanitize, then take the DOM's textContent.
 */
export function markdownToPlainText(markdown) {
	if (typeof markdown !== 'string' || markdown.trim() === '') return '';
	const container = document.createElement('div');
	container.innerHTML = renderMarkdown(markdown);
	return (container.textContent || '').replace(/\s+/g, ' ').trim();
}

// ---------------------------------------------------------------------------
// Documentation guides (/docs). Authored in the repo (docs/guides), never by a
// user, but sanitized all the same — this file is the one place DOMPurify
// runs. They differ from an element value in three ways: a single newline is
// NOT a break (the sources are hard-wrapped prose), tables are allowed, and
// headings carry an id so "On this page" and #fragments can land on them.
// ---------------------------------------------------------------------------

/** 'Before you start' -> 'before-you-start'. Shared with the page's outline. */
export function headingSlug(text) {
	return String(text || '')
		.toLowerCase()
		.replace(/[`*_]/g, '')
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');
}

const docParser = new Marked({ gfm: true, breaks: false });
docParser.use({
	renderer: {
		heading({ tokens, depth, text }) {
			return `<h${depth} id="${headingSlug(text)}">${this.parser.parseInline(tokens)}</h${depth}>\n`;
		},
	},
});

const DOC_TAGS = [...ALLOWED_TAGS, 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'kbd'];
const DOC_ATTR = [...ALLOWED_ATTR, 'id', 'align'];

/** Render a documentation guide's markdown to sanitized HTML. */
export function renderDocMarkdown(markdown) {
	if (typeof markdown !== 'string' || markdown.trim() === '') return '';
	const html = docParser.parse(markdown, { async: false });
	return DOMPurify.sanitize(html, { ALLOWED_TAGS: DOC_TAGS, ALLOWED_ATTR: DOC_ATTR });
}
